import * as THREE from 'three';
import { EngineDemoApp } from './EngineDemoApp';
import { Logger } from '../core/Logger';
import { POIData } from '../poi/POIData';
import { POI } from '../poi/POI';
import { Color, Vector3, TextRenderMode } from '../types';
import { InputEventManager } from '../core/InputEventManager';
import { UIUpdateManager } from '../../ui/UIUpdateManager';
import { AdvancedPOICamera } from '../camera/AdvancedPOICamera';
import { FlyCamera } from '../cameras/FlyCamera';
import { LoadingPhase } from '../core/LoadingManager';
// import { sdkStorageProvider } from '../../services/storageProvider';
import { AssetLoadingBus } from '../core/AssetLoadingBus';
import { LabelParentMode } from '../core/AppSettingsData';
import { ScreenPosition } from '../shapes/ScreenHTMLStaticLabelMesh';
import { EnvironmentRoot } from '../environment/EnvironmentRoot';
import { CloudRenderMode, CloudType } from '../environment/types';
import { MapBridge, BridgePin, BridgeRoute, BridgeGeoref } from '../../embed/MapBridge';
import { loadTscheppaMapPins } from '../../embed/TscheppaMapContent';
import { GeoModelTransform } from '../../embed/GeoModelTransform';
import { TpsWarp, TpsAnchor } from '../../embed/TpsWarp';
import { SkySun } from '../environment/SkySun';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { MapCategoryPinNode } from '../map/markers/MapCategoryPinNode';
import { MapPoiStackPinNode, gorgeStackRank, isGorgeStackPin } from '../map/markers/MapPoiStackPinNode';
import { MapDecisionPinNode } from '../map/markers/MapDecisionPinNode';
import { MapMarkerPoleNode } from '../map/markers/MapMarkerGeometry';
import { classifyMapMarker } from '../map/markers/MapMarkerClassifier';
import type { IMapSurface } from '../map/MapSurface';
import { GlbMapSurface } from '../map/surfaces/GlbMapSurface';
import { MapExperienceController } from '../map/MapExperienceController';
import { MapTapController } from '../map/MapTapController';
import { MapUserPositionRenderer } from '../map/MapUserPositionRenderer';
import { MapTubeRouteRenderer } from '../map/MapTubeRouteRenderer';
import { configureMapPresentationSettings } from '../map/MapPresentationSettings';
import { resolveMapMarkerMetrics, type MapMarkerMetricsWorld } from '../map/markers/MapMarkerMetrics';
import { buildCategoryStackLayout, buildExplicitStackLayout } from '../map/markers/MapStackLayout';
import wptGlbPositions from '../../embed/wptGlbPositions.json';
import terrainMask from '../../embed/terrainMask.json';
import wptGlbCalib from '../../embed/wptGlbCalib.json';
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from 'three-mesh-bvh';

// Accelerated raycasting: BVH-backed raycasts are ~1000x faster than three.js' brute-force
// triangle scan — required for the per-frame camera-vs-terrain collision on a 800k-tri map.
(THREE.BufferGeometry.prototype as any).computeBoundsTree = computeBoundsTree;
(THREE.BufferGeometry.prototype as any).disposeBoundsTree = disposeBoundsTree;
(THREE.Mesh.prototype as any).raycast = acceleratedRaycast;

/**
 * MapPinsApp
 *
 * A code-first variant of the demo app that:
 * - skips markdown parsing
 * - loads a GLB map model
 * - prepares the scene for later pin injection
 *
 * Usage: instantiate like EngineDemoApp; the UI can switch via query param.
 */
export class MapPinsApp extends EngineDemoApp {
    // Map-only photographic lighting. The generic presenter rig contributes three
    // directionals at intensity 2 each; adding both EnvironmentRoot's decorative sun and
    // SkySun on top produced 8.7 light units and clipped the aerial texture nearly white.
    // Keep the physical sky, but light this photographic map with one restrained solar key.
    private static readonly _MAP_TONE_MAPPING_EXPOSURE = 0.72;
    private static readonly _MAP_SKY_LIGHT_INTENSITY = 0.35;
    // --- Camera terrain collision ---
    private _terrainMeshes: THREE.Mesh[] = [];
    // Strict ground surface (Terrain CUTOUT only) — pin placement / cut-out tests
    // raycast against THIS, not the broad camera-collision set (which includes the
    // border skirt and underlay planes that would make everything look on-island).
    private _groundMeshes: THREE.Mesh[] = [];
    private _terrainRay = new THREE.Raycaster();
    private _rayOrigin = new THREE.Vector3();
    private _mapSurface: IMapSurface | null = null;
    private _userPositionRenderer: MapUserPositionRenderer | null = null;
    private _routeRenderer: MapTubeRouteRenderer | null = null;
    private _markerMetrics: MapMarkerMetricsWorld | null = null;
    private static readonly _DOWN = new THREE.Vector3(0, -1, 0);
    private static readonly _CAM_TERRAIN_CLEARANCE = 0.025; // map units above ground

    // Low-pass-filtered ground height under the camera. Smooths out the jagged low-poly
    // terrain so the collision response doesn't bump the camera on every mesh edge.
    private _smoothGroundY: number | null = null;
    // World-space floor: lowest terrain point. When the camera is OUTSIDE the island's
    // footprint the down-ray hits nothing and the ground clamp can't work — this absolute
    // floor still prevents flying/orbiting underneath the island.
    private _terrainFloorY: number | null = null;

    /**
     * Resolve a name-independent terrain fallback for optimized exports. The previous
     * fallback used every collision mesh, so a vertical ray above the entrance could hit
     * CubeStartRestaurant before the landscape. A dominant, wide/high-detail surface is a
     * safe fallback; localized props can never enter the POI height raycast again.
     */
    private selectDominantGroundMesh(): THREE.Mesh | null {
        let best: { mesh: THREE.Mesh; score: number } | null = null;
        for (const mesh of this._terrainMeshes) {
            try {
                const box = new THREE.Box3().setFromObject(mesh);
                const size = box.getSize(new THREE.Vector3());
                const area = Math.max(0, size.x * size.z);
                const span = Math.max(size.x, size.z, 1e-6);
                const positionCount = mesh.geometry.getAttribute('position')?.count ?? 0;
                const triangleCount = mesh.geometry.index
                    ? mesh.geometry.index.count / 3
                    : positionCount / 3;
                // Prefer broad, detailed, predominantly horizontal surfaces.
                const score = area * Math.log10(triangleCount + 10) / (1 + size.y / span);
                if (!best || score > best.score) best = { mesh, score };
            } catch { /* malformed helper meshes are not terrain candidates */ }
        }
        return best?.mesh ?? null;
    }

    /**
     * Camera-vs-terrain collider: runs every frame AFTER the POI camera has written its
     * position (super.onUpdate). Casts a ray straight down onto the terrain BVH and keeps
     * the camera above ground + clearance. Two-stage smoothing:
     *  1. the sampled ground height is low-pass filtered (absorbs low-poly jaggies),
     *  2. the camera eases toward the limit instead of hard-snapping,
     * with a hard floor slightly below the smoothed limit so it never visibly clips through.
     */
    protected override onUpdate(deltaTime: number): void {
        super.onUpdate(deltaTime);
        // Camera collision must use the same strict landscape surface as POI projection.
        // The broad mesh set contains the visible restaurant/bus/parking cubes and panels;
        // letting those participate lifts the camera onto a prop and makes ground-level POIs
        // appear to sit on top of it.
        if (this._groundMeshes.length === 0) return;
        const threeCam = (this.scene as any)?.camera as THREE.Camera | undefined;
        if (!threeCam) return;
        this._rayOrigin.set(threeCam.position.x, threeCam.position.y + 1000, threeCam.position.z);
        this._terrainRay.set(this._rayOrigin, MapPinsApp._DOWN);
        const hits = this._terrainRay.intersectObjects(this._groundMeshes, false);
        if (hits.length === 0) {
            this._smoothGroundY = null;
            // Outside the island footprint: enforce the absolute floor so the camera can
            // never dip below the island's underside.
            if (this._terrainFloorY !== null && threeCam.position.y < this._terrainFloorY) {
                threeCam.position.y = this._terrainFloorY;
            }
            return;
        }

        const targetMinY = hits[0].point.y + MapPinsApp._CAM_TERRAIN_CLEARANCE;
        const dt = Math.min(Math.max(deltaTime, 0.001), 0.1);

        // Stage 1: low-pass the ground limit (time-constant ~0.15s, framerate independent)
        this._smoothGroundY = this._smoothGroundY === null
            ? targetMinY
            : THREE.MathUtils.lerp(this._smoothGroundY, targetMinY, 1 - Math.exp(-dt / 0.15));

        if (threeCam.position.y < this._smoothGroundY) {
            // Stage 2: ease the camera up toward the limit (~0.1s time constant)...
            threeCam.position.y = THREE.MathUtils.lerp(threeCam.position.y, this._smoothGroundY, 1 - Math.exp(-dt / 0.1));
            // ...but never let it sink visibly below the actual ground.
            const hardFloor = targetMinY - MapPinsApp._CAM_TERRAIN_CLEARANCE * 0.6;
            if (threeCam.position.y < hardFloor) threeCam.position.y = hardFloor;
        }

        // Bridge: publish camera state to host (self-throttled to 250ms inside sendCameraChanged).
        if (this._bridge && this._geoTransform && this._mapRootObj) {
            try {
                const local = this._mapRootObj.worldToLocal(threeCam.position.clone());
                const geo = this._mapSurface?.worldToGeo(local);
                if (!geo) return;
                const groundY = this._smoothGroundY ?? 0;
                const euler = new THREE.Euler().setFromQuaternion(threeCam.quaternion, 'YXZ');
                this._bridge.sendCameraChanged({
                    lat: geo.lat,
                    lng: geo.lng,
                    distance: Math.max(0, threeCam.position.y - groundY),
                    heading: -THREE.MathUtils.radToDeg(euler.y),
                    pitch: THREE.MathUtils.radToDeg(euler.x),
                });
            } catch { /* non-fatal */ }
        }
    }
    protected override async onInitialize(): Promise<void> {
        //Logger.Instance.Info("🚀 MapPinsApp onInitialize() - code-first setup", "Initialization");

        if (!this.scene) {
            this.addError("Scene not initialized");
            return;
        }


        this.appSettings.hidePinSourceMeshes.value = false;
        // Embed mode starts with a bird's-eye overview so the host-pushed pins are visible
        // at first paint (Alex feedback: ground-level start hid all pins). Standalone keeps
        // the close-up start.
        const isEmbedStart = new URLSearchParams(window.location.search).get('embed') === '1';

        // Physical sky + real solar position for the gorge (46.487N 14.266E). Local time
        // by default; override with ?sun=<hour> (e.g. ?sun=7.5 for sunrise light).
        try {
            const sunParam = new URLSearchParams(window.location.search).get('sun');
            this._skySun = new SkySun(
                this.scene.getThreeScene(),
                46.487,
                14.266,
                MapPinsApp._MAP_SKY_LIGHT_INTENSITY,
            );
            this._skySun.applyForHour(sunParam !== null ? parseFloat(sunParam) : undefined);
        } catch (e) {
            Logger.Instance.Warn(`SkySun setup failed: ${e}`, 'Environment');
        }

        // Neutral environment map: fully-metallic materials in Alex' export (Steel.001 on
        // the signpost pole, the Deutscher-Peter info card) reflect the environment — with
        // none set they render black/invisible (the "missing silver pole").
        try {
            const renderer = (this.scene as any)?.getRenderer?.();
            if (renderer) {
                const pmrem = new THREE.PMREMGenerator(renderer);
                this.scene.getThreeScene().environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
                pmrem.dispose();
            }
        } catch (e) {
            Logger.Instance.Warn(`Environment map setup failed: ${e}`, 'Environment');
        }

        // The map uses a photographic terrain texture, which needs a much quieter rig than
        // the generic 3D-presentation defaults. This is intentionally scoped to MapPinsApp:
        // normal presentations keep their authored lights and exposure. RoomEnvironment
        // remains active so metallic signposts/cards retain their reflections.
        this.sceneSettings.ambientLightIntensity.value = 0.32;
        this.sceneSettings.keyLightIntensity.value = 0.75;
        this.sceneSettings.fillLightIntensity.value = 0.25;
        this.sceneSettings.backLightIntensity.value = 0.25;
        try {
            const renderer = this.scene.getRenderer();
            renderer.toneMappingExposure = MapPinsApp._MAP_TONE_MAPPING_EXPOSURE;
        } catch (e) {
            Logger.Instance.Warn(`Map tone mapping setup failed: ${e}`, 'Environment');
        }
        this.appSettings.cameraDistance.value = isEmbedStart ? 1.4 : 0.07;
        if (isEmbedStart) {
            // +55: camera ABOVE the target looking down. (-55 placed it below the island —
            // Alex device test: "I am down here which is wrong".)
            try { this.appSettings.cameraLatitude.value = 55; } catch {}
        }

        //das funktioniert nicht
        //this.appSettings.labelBackgroundGradient.value = 'linear';
        //this.appSettings.labelBackgroundColor.value = new Color('#ffffff');

        this.appSettings.enableBillboardForMapAnchors.value = false;
        this.appSettings.shapeBillboardYawOnly.value = false
        this.sceneSettings.videoAutoplay.value = true;

        this.appSettings.labelRenderMode.value = TextRenderMode.CANVAS;
        {
            // Map pins are viewed against a full terrain model, so the generic presenter
            // label scale is far too small on a phone (Alex' iPhone capture showed roughly
            // 4 px-high titles). Keep this override local to MapPinsApp: regular
            // presentations retain their authored/global scale.
            this.appSettings.labelScale.value = 0.04;
            this.appSettings.labelParentMode.value = LabelParentMode.POISHAPE;
            this.appSettings.fadeLabelOnActive.value = false;
            this.appSettings.labelDistance.value = 0.08;
        }

        this.appSettings.labelLongitude.value = 0;
        this.appSettings.labelLatitude.value = 0;
        this.appSettings.labelColor.value = new Color('#FFFFFF');
        // Fully transparent label background — the opaque card looked like a big white/grey box
        this.appSettings.labelBackgroundColor.value = new Color('#00000000');
        this.appSettings.labelMaxWidth.value = 500;
        this.appSettings.labelBillboardMode.value = false;
        this.sceneSettings.enableBillboard.value = true;


        this.sceneSettings.depth.value = 0.1;
        this.sceneSettings.cornerRadius.value = 0.2;
        this.sceneSettings.color.value = new Color('#FF0000');
        this.sceneSettings.edgeRadius.value = 0.3;

        this.sceneSettings.backgroundGradientColor1.value = new Color('#1a2a6c');
        this.sceneSettings.backgroundGradientColor2.value = new Color('#00c4f5');



        this.appSettings.showConnector.value = false
        this.appSettings.connectorColor.value = new Color('#ffffff')
        this.appSettings.connectorThickness.value = 1



        // Ensure no debug bounds are shown for clean map view
        try { (this.appSettings as any).showBounds.value = false; } catch { }
        // ==================== PHASE 1: SCENE SETUP ====================
        this.loadingManager.startPhase(LoadingPhase.SceneSetup, '🎪 Setting up 3D scene...');
        this.loadingManager.addTask('input-setup', 'Input Event Manager', 1);
        this.loadingManager.addTask('ui-manager', 'UI Manager', 1);

        // 1) InputEventManager binding (canvas + scene + provisional camera)
        if (this.canvas) {
            InputEventManager.Instance.setCanvas(this.canvas);
            InputEventManager.Instance.setScene(this.scene.getThreeScene());
            InputEventManager.Instance.setCamera(this.scene.getThreeScene().children.find(obj => obj instanceof THREE.Camera) as THREE.Camera);
            //Logger.Instance.Info("🎮 InputEventManager configured with canvas for mouse wheel zoom", "Input");

            // Keep wheel and pinch on the same canonical cameraDistance path. Pinch is
            // normalized by InputManager and consumed by EngineDemoApp's input:wheel hook.
            this.canvas.addEventListener('wheel', (event) => {
                event.preventDefault();
                this.applyWheelZoom(event.deltaY);
            }, { passive: false });

            // Focus management for keyboard + wheel events
            this.canvas.tabIndex = 0;
            this.canvas.style.outline = 'none';
            this.canvas.addEventListener('click', () => this.canvas?.focus());
            this.canvas.focus();
        }
        this.loadingManager.completeTask('input-setup');

        // 2) UI Manager node
        //Logger.Instance.Info("🎮 Adding UI Manager...", "UI");
        this.sceneManager?.addSceneNode(new UIUpdateManager());
        this.loadingManager.completeTask('ui-manager');
        this.loadingManager.completePhase();

        // ==================== PHASE 2: CAMERA & AR ====================
        this.loadingManager.startPhase(LoadingPhase.CameraSetup, '🎥 Setting up camera system...');
        this.loadingManager.addTask('camera-creation', 'Advanced POI Camera', 1);
        this.loadingManager.addTask('ar-setup', 'AR System Setup', 1);

        // Create and bind camera (use base private via any-cast)
        //Logger.Instance.Info("🎥 Creating camera...", "Camera");
        const advCam = new AdvancedPOICamera(this.scene.getThreeScene(), this.appSettings, this.systemState);
        (this as any).camera = advCam;
        this.scene.setCamera(advCam.internalCamera);
        InputEventManager.Instance.setCamera(advCam.internalCamera);
        this.loadingManager.completeTask('camera-creation');

        // Initialize AR manager (reusing base instance created in constructor)
        //Logger.Instance.Info("🥽 Initializing AR Manager...", "AR");
        const flyCam = new FlyCamera('SpectatorCamera', this.appSettings);
        (this as any).flyCamera = flyCam;
        this.cameraManager.addCamera(flyCam);
        flyCam.setRenderCamera(advCam.internalCamera);
        this.arManager.initialize(
            this.scene.getRenderer(),
            this.scene.getThreeScene(),
            advCam.internalCamera,
            advCam
        );
        this.loadingManager.completeTask('ar-setup');
        this.loadingManager.completePhase();

        // (Environment is attached after map POI is created to inherit map scale)

        // ==================== PHASE 3: CODE-FIRST MAP LOAD ====================
        this.loadingManager.startPhase(LoadingPhase.POILoading, '🗺️ Loading GLB map (code-first)...');
        this.loadingManager.addTask('create-map-poi', 'Create map POI', 1);

        try {
            const mapName = 'Map_Alps';
            // Resolve storage object id via Storage singleton (cached).
            // 114640 = Alex1.glb: Alex' own full export — VERBATIM. Do not swap in
            // "cleaned" re-exports: his newer content objects (icon towers, info panels)
            // are not in older name whitelists and get silently dropped (2026-07-21
            // regression). Calibration ref (wptGlbCalib.json) is extracted from THIS file;
            // the artrack id-join positions are world-space and file-independent.
            const mapObjectId = 114640;
            // Always read the public map through presenter's same-origin Storage proxy.
            // Direct api-storage URLs are blocked by Chrome's Local Network Access policy
            // on arkserver/LAN devices before any POI can initialize.
            const metadataResponse = await fetch(`/storage-api/storage/objects/${mapObjectId}`);
            if (!metadataResponse.ok) throw new Error(`Map metadata ${metadataResponse.status}`);
            const mapMetadata = await metadataResponse.json();
            const version = encodeURIComponent(String(mapMetadata.updated_at || mapObjectId));
            // Desktop gets the raw export (full texture quality, Alex' wish). Mobile gets a
            // high-quality optimized variant (~5 MB vs 53 MB) — the raw file made phones sit
            // in "3D-Karte verbindet..." for minutes on cellular.
            const isMobileClient = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
            const rawUri = `/storage-api/storage/media/${mapObjectId}?v=${version}`;
            const mapUrl = isMobileClient
                ? rawUri + '&preset=web&texture_max_size=2048&texture_quality=90'
                : rawUri;

            const poidata = new POIData(this.sceneSettings, this.appSettings);
            poidata.shape.value = 'gltf';
            poidata.uri.value = [mapUrl];
            poidata.markdown.value = `# Map\nGLB loaded from ${mapUrl}`;
            poidata.scale.value = 0.001;
            poidata.position.value = new Vector3(0, 0, 0);
            poidata.color.value = new Color('#ffffff');
            poidata.showLabel.value = false;
            poidata.enableBillboard.value = false;
            // Place at origin by default
            poidata.position.value = new Vector3(0, 0, 0);

            const mapPOI = new POI(mapName, poidata, this.sceneSettings, this.appSettings, this.getLayoutData());
            this.addPOI(mapPOI);

            // Attach environment under the map root so it inherits map transform/scale
            // Debug bisect flag: ?noenv=1 skips clouds/birds/lightning entirely (mobile
            // crash diagnosis — 99 animated birds + particle clouds are a prime suspect).
            const skipEnv = new URL(window.location.href).searchParams.get('noenv') === '1';
            try {
                if (skipEnv) throw new Error('env disabled via ?noenv=1');
                const env = new EnvironmentRoot({
                    clouds: {
                        density: 0.3,
                        speed: 0.1,
                        height: 30,
                        spreadX: 480,        // wider horizontally
                        spreadZ: 300,
                        type: CloudType.CUMULUS,
                        mode: CloudRenderMode.PARTICLES,
                        hybridOpacity: 0.3,
                        textureUrls: [
                            '/assets/in_Wolke1.png',
                            '/assets/in_Wolke2.png',
                            '/assets/in_Wolke3.png',
                            '/assets/in_Wolke4.png'
                        ],
                        spriteSizeRange: [2.0, 4.5]
                    },
                    birds: {
                        count: 99,
                        speed: 0.03,
                        height: 17,
                        detailedThreshold: 150,
                        bounds: { xz: 40, yMin: 14, yMax: 32 },
                        sizeScale: 0.6 // 60% der bisherigen Größe
                    },
                    lightning: {
                        randomMinMs: 3000,
                        randomMaxMs: 8000,
                        boltSegments: 30,
                        branchCount: 5,
                        zigzagStrength: 6,
                        fadePerSec: 2.5,
                        flashIntensity: 3.0,
                        flashHoldMs: 400
                    },
                    // No EnvironmentRoot sun here: SkySun already supplies both the visible
                    // solar disc and the time-aware directional key. The previous duplicate
                    // sun was the main source of clipped whites and washed-out map colors.
                });
                env.setScaleUniform(0.02); // z.B. 1000
                mapPOI.addChild(env);
            } catch { }


            this.setActivePOI(mapPOI);

            // Defer node debug + pin scan until GLB actually finished loading
            const onLoaded = async (payload: any) => {
                try {
                    if (payload?.type === 'gltf' && payload?.poi === mapPOI) {
                        this.debugListGltfNodes(mapPOI);

                        // Collect terrain meshes for camera collision and build their BVH.
                        // Exclude pin/label/pointer props so flying toward a pin doesn't
                        // shove the camera up over the pin's head.
                        try {
                            const rootObjs = mapPOI.getThreeObjects();
                            const mapRoot = (rootObjs && rootObjs[0]) as THREE.Object3D | undefined;
                            if (mapRoot) {
                                const excluded = /wpt|pin|point|pointer|label|stand|schild|environmentroot|sunsystem|cloud/i;
                                mapRoot.traverse((o: any) => {
                                    const hierarchyNames: string[] = [];
                                    const hierarchyObjects: THREE.Object3D[] = [];
                                    let ancestor: THREE.Object3D | null = o;
                                    while (ancestor) {
                                        hierarchyNames.push(ancestor.name || '');
                                        hierarchyObjects.push(ancestor);
                                        if (ancestor === mapRoot) break;
                                        ancestor = ancestor.parent;
                                    }
                                    const isExcluded = hierarchyNames.some(name => excluded.test(name));
                                    if (o?.isMesh && o.geometry && !isExcluded) {
                                        try { o.geometry.computeBoundsTree?.(); } catch {}
                                        this._terrainMeshes.push(o);
                                        const materials = Array.isArray(o.material) ? o.material : [o.material];
                                        const hasTerrainMaterial = materials.some((material: THREE.Material | undefined) =>
                                            /^overlay$/i.test(material?.name || '')
                                        );
                                        const hasTerrainRole = hierarchyObjects.some(object => {
                                            const data = object.userData || {};
                                            const role = String(data.presenter_role ?? data.presenterRole ?? data.collision_role ?? data.collisionRole ?? '');
                                            return /^(terrain|ground)$/i.test(role);
                                        });
                                        // Draco/gltfpack can insert unnamed primitive Groups between the
                                        // exported node and its Mesh, or strip those node names altogether.
                                        // `overlay` is the unique Terrain CUTOUT material in Alex' export;
                                        // presenter_role=terrain is the stable contract for future exports.
                                        if (hasTerrainRole || hasTerrainMaterial || hierarchyNames.some(name => /^Terrain[\s_.-]*CUTOUT/i.test(name))) {
                                            this._groundMeshes.push(o);
                                        }
                                    }
                                });
                                if (this._groundMeshes.length === 0) {
                                    const fallback = this.selectDominantGroundMesh();
                                    if (fallback) {
                                        this._groundMeshes.push(fallback);
                                        Logger.Instance.Warn(
                                            `Terrain CUTOUT name unavailable; using dominant surface "${fallback.name || '(unnamed)'}" for POI heights`,
                                            'Collision'
                                        );
                                    }
                                }
                                this._terrainRay.firstHitOnly = true as any;
                                try {
                                    const bbox = new THREE.Box3();
                                    for (const m of this._groundMeshes) bbox.expandByObject(m);
                                    this._terrainFloorY = bbox.min.y + MapPinsApp._CAM_TERRAIN_CLEARANCE;
                                } catch {}
                                Logger.Instance.Info(
                                    `Camera terrain collision armed: ${this._terrainMeshes.length} collision meshes, ${this._groundMeshes.length} strict ground mesh(es) (BVH), floorY=${this._terrainFloorY?.toFixed(3)}`,
                                    'Collision'
                                );
                            }
                        } catch (e) {
                            Logger.Instance.Warn(`Terrain collision setup failed: ${e}`, 'Collision');
                        }

                        // Create POIs from the server-sanitized ArTrack feed. GLB WPT nodes
                        // are position anchors only — their legacy 1365 suffix is not content.
                        // Debug bisect flag: ?nopins=1 skips pin creation (45 POIs with canvas labels).
                        // Embed mode (?embed=1, Bridge Contract v0.2 §9): GLB-baked WPT pins are
                        // DISABLED as visible pins — the host is the single pin source via setPins.
                        // WPT nodes remain in the scene graph solely as georef calibration material.
                        // Always-hide list: pipeline artifacts in Alex' verbatim export that render
                        // wrong (Section_Barfußparcours got snapped onto the wrong Wanderweg path and
                        // cuts across the hillside as a stray green curve).
                        try {
                            const objsJ = mapPOI.getThreeObjects();
                            const rootJ = (objsJ && objsJ[0]) as THREE.Object3D | undefined;
                            rootJ?.traverse((o: any) => {
                                if (/^Section_Barfußparcours/i.test(o?.name || '')) o.visible = false;
                            });
                        } catch {}
                        const sp = new URL(window.location.href).searchParams;
                        if (sp.get('nopins') === '1' || sp.get('embed') === '1') {
                            Logger.Instance.Info(`Pin feed skipped (${sp.get('embed') === '1' ? 'embed mode — host owns pins' : '?nopins=1'})`, 'Init');
                            // In embed mode also hide the GLB-baked pin props (pin stands, signs,
                            // pointer meshes) — otherwise they'd render as a second, stale pin set
                            // next to the host-pushed pins.
                            if (sp.get('embed') === '1') {
                                try {
                                    const objs2 = mapPOI.getThreeObjects();
                                    const root2 = (objs2 && objs2[0]) as THREE.Object3D | undefined;
                                    const pinProps = /wpt|pin|pointer|stand|schild|point\./i;
                                    let hidden = 0;
                                    root2?.traverse((o: any) => {
                                        if (o?.isMesh && (pinProps.test(o.name || '') || pinProps.test(o.parent?.name || ''))) {
                                            o.visible = false; hidden++;
                                        }
                                    });
                                    Logger.Instance.Info(`Embed: hid ${hidden} GLB pin prop meshes`, 'Init');
                                } catch {}
                                // Terrain + BVH are ready — bring up the bridge and tell the host.
                                try {
                                    const objs3 = mapPOI.getThreeObjects();
                                    const root3 = (objs3 && objs3[0]) as THREE.Object3D | undefined;
                                    if (root3) this.startEmbedBridge(root3);
                                } catch (e) {
                                    Logger.Instance.Error(`Embed bridge start failed: ${e}`, 'Bridge');
                                }
                            }
                            AssetLoadingBus.off('asset:load-complete', onLoaded);
                            return;
                        }
                        try {
                            const root = mapPOI.getThreeObjects()?.[0] as THREE.Object3D | undefined;
                            if (root) await this.startStandaloneMapContent(root);
                        } catch (error) {
                            Logger.Instance.Error(`Standalone map content failed: ${error}`, 'MapContent');
                            this.showStandaloneFeedError();
                        }
                        AssetLoadingBus.off('asset:load-complete', onLoaded);
                    }
                } catch { }
            };
            AssetLoadingBus.on('asset:load-complete', onLoaded);
        } catch (err) {
            Logger.Instance.Error(`❌ Failed to create map POI: ${err}`, 'Initialization');
        }

        this.loadingManager.completeTask('create-map-poi');
        this.loadingManager.completePhase();

        // ==================== PHASE 4: MATERIAL LOADING ====================
        this.loadingManager.startPhase(LoadingPhase.MaterialLoading, '🎨 Loading textures and videos...');
        this.loadingManager.addTask('material-registration', 'Register materials for loading', 1);
        this.loadingManager.addTask('material-loading', 'Load all materials', 3);

        await this.registerAllMaterialsForLoading();
        this.loadingManager.completeTask('material-registration');
        // Trigger actual texture/video loads after registration (as in EngineDemoApp)
        if ((this as any).triggerTextureLoadingForAllPOIs) {
            (this as any).triggerTextureLoadingForAllPOIs();
        }
        await this.waitForAllMaterialsToLoad();
        this.loadingManager.completeTask('material-loading');
        this.loadingManager.completePhase();

        // ==================== PHASE 5: LAYOUT APPLICATION ====================
        this.loadingManager.startPhase(LoadingPhase.LayoutApplication, '🎯 Applying layout system...');
        this.loadingManager.addTask('layout-setup', 'Layout system setup', 1);

        // Prefer map-anchor layouter
        try {
            const layoutData = this.getLayoutData();
            layoutData.layouter = 'map-anchor';
            layoutData.layouterProperties = layoutData.layouterProperties || {};
            (layoutData.layouterProperties as any).mapName = 'Map_Alps';
            // Disable process curve/numbering for map-anchor presentations
            configureMapPresentationSettings(this.sceneSettings);
        } catch { }

        this.setupLayoutSystem();
        // Apply layout if not using field awareness
        if (this.pois.length > 0 && !this.getLayoutData().fieldAwarenessEnabled.value) {
            this.applyLayoutWithProcessCurveUpdate();
        }
        this.loadingManager.completeTask('layout-setup');
        this.loadingManager.completePhase();

        // ==================== PHASE 6: READY ====================
        this.loadingManager.startPhase(LoadingPhase.Ready, '🚀 Preparing to launch...');
        this.loadingManager.addTask('final-setup', 'Final preparations', 1);
        await new Promise(resolve => setTimeout(resolve, 300));
        this.loadingManager.completeTask('final-setup');
        this.loadingManager.complete();

        await this.loadingManager.waitUntilReady();

        //Logger.Instance.Info("✅ MapPinsApp initialized (code-first map loaded)", "Initialization");
    }

    /**
     * Print a flattened list of GLTF nodes (name, type) for inspection in console.
     */
    private debugListGltfNodes(mapPOI: POI): void {
        const objs = mapPOI.getThreeObjects();
        const root = (objs && objs[0]) as THREE.Object3D | undefined;
        if (!root) return;
        const items: Array<{
            name: string;
            geomName?: string;
            type: string;
            path: string;
            pos: string;
            children: number;
            materials?: string[];
            geom?: { posCount?: number; indexCount?: number; triCount?: number; bbox?: string; bsphere?: string };
            userDataKeys?: string[];
        }> = [];
        const tmpPos = new THREE.Vector3();
        const tmpBox = new THREE.Box3();
        const materialSet = new Set<string>();
        const stack: { obj: THREE.Object3D; path: string }[] = [{ obj: root, path: root.name || 'root' }];
        while (stack.length) {
            const { obj, path } = stack.pop()!;
            obj.getWorldPosition(tmpPos);
            const entry: any = {
                name: obj.name || '(unnamed)',
                geomName: undefined,
                type: obj.type,
                path,
                pos: `${tmpPos.x.toFixed(3)},${tmpPos.y.toFixed(3)},${tmpPos.z.toFixed(3)}`,
                children: obj.children.length,
                userDataKeys: Object.keys((obj as any).userData || {})
            };
            // Mesh-specific details
            if ((obj as any).isMesh) {
                const mesh = obj as THREE.Mesh;
                const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                const matNames = mats.map(m => (m && (m as any).name) ? (m as any).name as string : '(unnamed)');
                matNames.forEach(n => materialSet.add(n));
                entry.materials = matNames;
                const g = mesh.geometry as THREE.BufferGeometry;
                if (g) {
                    entry.geomName = (g as any).name || undefined;
                    const posAttr = g.getAttribute('position');
                    const posCount = posAttr ? posAttr.count : undefined;
                    const indexCount = g.index ? g.index.count : undefined;
                    const triCount = indexCount ? Math.floor(indexCount / 3) : (posCount ? Math.floor(posCount / 3) : undefined);
                    // Bounding info
                    if (!g.boundingBox) g.computeBoundingBox();
                    const box = g.boundingBox || tmpBox.makeEmpty();
                    const bboxStr = `${box.min.x.toFixed(3)},${box.min.y.toFixed(3)},${box.min.z.toFixed(3)} -> ${box.max.x.toFixed(3)},${box.max.y.toFixed(3)},${box.max.z.toFixed(3)}`;
                    if (!g.boundingSphere) g.computeBoundingSphere();
                    const bs = g.boundingSphere;
                    const bsStr = bs ? `${bs.center.x.toFixed(3)},${bs.center.y.toFixed(3)},${bs.center.z.toFixed(3)} r=${bs.radius.toFixed(3)}` : '';
                    entry.geom = { posCount, indexCount, triCount, bbox: bboxStr, bsphere: bsStr };
                }
            }
            items.push(entry);
            for (const child of obj.children) {
                stack.push({ obj: child, path: `${path}/${child.name || child.type}` });
            }
        }
        console.log('📦 GLB Node List (' + items.length + '):');
        items.slice(0, 300).forEach((it, i) => console.log(`${i.toString().padStart(3, '0')} | ${it.type.padEnd(10)} | node=${it.name} | geom=${it.geomName || '-'} | pos(${it.pos}) | kids=${it.children} | mats=[${(it.materials || []).join(', ')}] | ${it.geom ? `tri=${it.geom.triCount ?? '-'} bbox(${it.geom.bbox})` : ''} | ${it.path}`));
        if (items.length > 300) console.log(`...and ${items.length - 300} more`);
        // Also log unique material names for quick filtering
        console.log('🎨 Unique Materials:', Array.from(materialSet));
    }


    // ==================================================================================
    // EMBED BRIDGE (Contract v0.2, post 4263) — the frame side of /navigate-3d.
    // Started after the terrain GLB + BVH are ready so incoming pins can be placed
    // immediately. The host is the single source of all pins/routes.
    // ==================================================================================

    private static readonly _GLB_114637_GEOREF: BridgeGeoref = {
        originLat: 46.48714281118566,
        originLng: 14.268978077829424,
        metersPerUnit: 1.0171003319746625,
        rotationDeg: 0.6735574684346735,
        originModel: { x: -196.95790769245116, y: 0, z: 110.37944615510081 },
        zNorthPositive: false,
    };

    private _skySun: SkySun | null = null;
    private _bridge: MapBridge | null = null;
    private _mapExperience: MapExperienceController | null = null;
    private _mapTapController: MapTapController | null = null;
    private _geoTransform: GeoModelTransform | null = null;
    private _bridgeGeoref: BridgeGeoref | null = null;
    private _bridgePins: Map<number, POI> = new Map();
    private _bridgeActivePinId: number | null = null;
    private _standalonePins: Map<number, BridgePin> = new Map();
    private _standaloneSheet: HTMLElement | null = null;
    private _standaloneSheetStyle: HTMLStyleElement | null = null;
    private _standaloneHls: { destroy(): void } | null = null;
    private _standaloneMediaToken = 0;
    private _standaloneTapCleanup: (() => void) | null = null;
    private _standaloneAbort: AbortController | null = null;
    private _standaloneModeControls: HTMLElement | null = null;
    private _standaloneModeStyle: HTMLStyleElement | null = null;
    private _standaloneMode: 'presenter' | 'onsite' = 'presenter';
    private _standaloneModeStatus: HTMLElement | null = null;
    private _standalonePresenterButton: HTMLButtonElement | null = null;
    private _standaloneOnsiteButton: HTMLButtonElement | null = null;
    private _standaloneLocateButton: HTMLButtonElement | null = null;
    private _geolocationWatchId: number | null = null;
    private _geolocationInitialFocus = false;
    private _lastStandaloneFix: { lat: number; lng: number; heading?: number; accuracy?: number } | null = null;
    private _presenterCameraSnapshot: { distance: number; latitude: number } | null = null;
    // v0.3 category filter — full-replace re-render of the cached push; the TPS warp and
    // snap/anchor computation always use the FULL pin set so filtering never shifts geometry.
    private _categoryFilter: string[] | null = null;
    private _lastBridgePinsPush: BridgePin[] | null = null;
    private _categoryPinNodes: Map<number, MapCategoryPinNode> = new Map();
    private _gorgeStackPinNodes: Map<number, MapPoiStackPinNode> = new Map();
    private _decisionPinNodes: Map<number, MapDecisionPinNode> = new Map();
    private _iconPinPoles: Map<number, MapMarkerPoleNode> = new Map();
    private _mapRootObj: THREE.Object3D | null = null;

    // GLB-file-space → container-local transform (uniform scale + translation), self-calibrated
    // at bridge start from the WPT nodes present in the three scene graph. Necessary because
    // the georef calibration is expressed in GLB file coordinates, while the layouter operates
    // in the POI container's local space (the GLTF normalization scale sits between the two).
    private _glbScale = 1;
    private _glbOffset = new THREE.Vector3();

    private calibrateGlbToLocal(mapRoot: THREE.Object3D): boolean {
        const filePos = new Map<number, THREE.Vector3>();
        for (const e of wptGlbCalib as any[]) filePos.set(e.wpt, new THREE.Vector3(e.x, e.y, e.z));
        const pairs: { g: THREE.Vector3; c: THREE.Vector3 }[] = [];
        mapRoot.updateMatrixWorld(true);
        mapRoot.traverse((o: THREE.Object3D) => {
            const m = (o.name || '').match(/^WPT-(\d+)-\d+-SPHERE$/i);
            if (!m) return;
            const g = filePos.get(parseInt(m[1]));
            if (!g) return;
            const world = o.getWorldPosition(new THREE.Vector3());
            pairs.push({ g, c: mapRoot.worldToLocal(world.clone()) });
        });
        if (pairs.length < 3) {
            Logger.Instance.Warn(`GLB self-calibration failed: only ${pairs.length} WPT nodes found`, 'Bridge');
            return false;
        }
        const gMean = new THREE.Vector3(); const cMean = new THREE.Vector3();
        pairs.forEach(p => { gMean.add(p.g); cMean.add(p.c); });
        gMean.divideScalar(pairs.length); cMean.divideScalar(pairs.length);
        let num = 0, den = 0;
        pairs.forEach(p => { num += p.c.clone().sub(cMean).length(); den += p.g.clone().sub(gMean).length(); });
        this._glbScale = num / den;
        this._glbOffset = cMean.clone().sub(gMean.clone().multiplyScalar(this._glbScale));
        // Residual self-check: reject the scale+translation assumption if it doesn't hold.
        let worst = 0;
        pairs.forEach(p => {
            const pred = p.g.clone().multiplyScalar(this._glbScale).add(this._glbOffset);
            worst = Math.max(worst, pred.distanceTo(p.c));
        });
        const spanLocal = this._glbScale * 2200;
        Logger.Instance.Info(`GLB self-calibration: ${pairs.length} WPTs, scale=${this._glbScale.toFixed(6)}, worst residual=${worst.toFixed(4)} (${(100 * worst / spanLocal).toFixed(2)}% of span)`, 'Bridge');
        return worst < spanLocal * 0.02; // <2% of extent, else rotation is involved and we bail
    }

    private glbToLocal(x: number, y: number, z: number): THREE.Vector3 {
        return new THREE.Vector3(x, y, z).multiplyScalar(this._glbScale).add(this._glbOffset);
    }

    private glbFromLocal(local: THREE.Vector3): THREE.Vector3 {
        return local.clone().sub(this._glbOffset).divideScalar(this._glbScale);
    }

    // Terrain footprint mask (rasterized offline from the raw GLB's Terrain CUTOUT mesh).
    // The optimized GLB loses node names (gltfpack), so the cut-out test cannot rely on
    // picking the right mesh at runtime — the mask is name-independent and exact.
    private static _maskBits: Uint8Array | null = null;
    /** Browsers on LAN devices resolve api-storage.arkserver to a private IP (NAT-hairpin
     *  /etc/hosts workaround) and Chrome blocks public->local subresource fetches (Local
     *  Network Access). presenter.arkturian.com proxies that storage instance same-origin,
     *  which works for LAN and external clients alike. */
    private static normalizeMediaUrl(url: string): string {
        try {
            const parsed = new URL(url, window.location.href);
            if (parsed.hostname === 'api-storage.arkserver.arkturian.com') {
                if (parsed.pathname.startsWith('/storage/')) {
                    return `${window.location.origin}/storage-arkserver/${parsed.pathname.slice('/storage/'.length)}${parsed.search}`;
                }
                // Keep HLS and its relative segment URLs same-origin as well; direct VOD
                // resolves to a private address inside the LAN and triggers LNA blocking.
                if (parsed.pathname.startsWith('/uploads/storage/')) {
                    return `${window.location.origin}/storage-api${parsed.pathname}${parsed.search}`;
                }
            }
            if (parsed.hostname === 'api-storage.arkturian.com') {
                return `${window.location.origin}/storage-api${parsed.pathname}${parsed.search}`;
            }
        } catch { /* retain the original URL below */ }
        return url;
    }

    private initializeMapSpace(mapRoot: THREE.Object3D): void {
        this._mapRootObj = mapRoot;
        this.hideBakedCategoryPinPrototypes(mapRoot);
        if (!this.calibrateGlbToLocal(mapRoot)) {
            Logger.Instance.Error('GLB self-calibration rejected — pin placement would be wrong', 'Bridge');
        }
        const georef = MapPinsApp._GLB_114637_GEOREF;
        this._bridgeGeoref = georef;
        this._geoTransform = new GeoModelTransform(georef);
        this._mapSurface = new GlbMapSurface({
            worldUnitsPerMeter: () => this._glbScale / (this._bridgeGeoref?.metersPerUnit ?? 1),
            geoToWorld: (lat, lng, elevationMeters = 0) => {
                if (!this._geoTransform) return null;
                const glb = this.geoToGlb(lat, lng);
                const local = this.glbToLocal(glb.x, 0, glb.z);
                local.y += elevationMeters * (this._glbScale / (this._bridgeGeoref?.metersPerUnit ?? 1));
                return local;
            },
            worldToGeo: (world) => {
                if (!this._geoTransform) return null;
                const glb = this.glbFromLocal(world);
                return this.glbToGeo(glb.x, glb.z);
            },
            getWorldY: (x, z) => this.terrainLocalY(x, z),
            containsWorld: (x, z) => {
                const glb = this.glbFromLocal(new THREE.Vector3(x, 0, z));
                return MapPinsApp.maskInside(glb.x, glb.z);
            },
        });
        this._userPositionRenderer?.dispose();
        this._userPositionRenderer = new MapUserPositionRenderer({
            surface: this._mapSurface,
            parent: mapRoot,
        });
        this._routeRenderer?.dispose();
        this._routeRenderer = new MapTubeRouteRenderer({
            surface: this._mapSurface,
            parent: mapRoot,
        });
        this._markerMetrics = resolveMapMarkerMetrics(this._mapSurface);
    }

    /**
     * Alex' GLB contains the original food/bus/parking cube-stack prototype. The same
     * symbols are now generated from ArTrack data, so retaining the baked prototype would
     * render a stale duplicate and would keep participating in scene raycasts.
     */
    private hideBakedCategoryPinPrototypes(mapRoot: THREE.Object3D): void {
        const prototypeName = /^(?:OldSchoolPinStand|CubeStartRestaurant|Bus Haltestelle Am Start)/i;
        let hidden = 0;
        mapRoot.traverse((object: THREE.Object3D) => {
            let current: THREE.Object3D | null = object;
            let belongsToPrototype = false;
            while (current && current !== mapRoot) {
                if (prototypeName.test(current.name || '')) {
                    belongsToPrototype = true;
                    break;
                }
                current = current.parent;
            }
            if (belongsToPrototype && object.visible) {
                object.visible = false;
                hidden++;
            }
        });
        if (hidden > 0) {
            Logger.Instance.Info(`Map category pins: hid ${hidden} baked prototype objects; ArTrack is authoritative`, 'MapPins');
        }
    }

    /** Standalone app=map uses the same placement pipeline as the embed bridge, but reads
     *  its sanitized public content from presenter-api and owns the local detail sheet. */
    private async startStandaloneMapContent(mapRoot: THREE.Object3D): Promise<void> {
        this.initializeMapSpace(mapRoot);
        this._standaloneAbort?.abort();
        this._standaloneAbort = new AbortController();
        const pins = await loadTscheppaMapPins(this._standaloneAbort.signal);
        this._standalonePins = new Map(pins.map(pin => [pin.id, pin]));
        this.applyBridgePins(pins);
        this.installStandaloneTapHandler();
        this.installStandaloneModeControls();
        Logger.Instance.Info(`Standalone map: loaded ${pins.length} real ArTrack POIs`, 'MapContent');
    }

    /** Standalone-only mode switch: POI storytelling remains the default; on-site mode
     *  starts a browser geolocation watch and plots the fix through the same geo/TPS/terrain
     *  pipeline as routes and POIs. Permission is requested only when the user activates it. */
    private installStandaloneModeControls(): void {
        if (this._standaloneModeControls) return;
        const style = document.createElement('style');
        style.textContent = `
          .tscheppa-map-mode{position:fixed;z-index:10015;left:max(14px,env(safe-area-inset-left));top:max(14px,env(safe-area-inset-top));display:grid;grid-template-columns:auto auto auto;align-items:center;gap:4px;padding:5px;border:1px solid rgba(255,255,255,.22);border-radius:18px;background:rgba(7,17,31,.86);box-shadow:0 12px 38px rgba(0,0,0,.32);backdrop-filter:blur(16px);font-family:Inter,system-ui,sans-serif;color:#fff}
          .tscheppa-map-mode button{min-height:38px;padding:0 13px;border:0;border-radius:13px;background:transparent;color:rgba(255,255,255,.76);font:700 13px/1 system-ui,sans-serif;cursor:pointer;white-space:nowrap}
          .tscheppa-map-mode button[aria-pressed="true"]{background:#fff;color:#0a1728;box-shadow:0 3px 12px rgba(0,0,0,.22)}
          .tscheppa-map-mode__locate{display:none!important;background:#2f7cff!important;color:#fff!important}
          .tscheppa-map-mode[data-mode="onsite"][data-has-fix="true"] .tscheppa-map-mode__locate{display:block!important}
          .tscheppa-map-mode__status{grid-column:1/-1;padding:2px 8px 4px;color:rgba(255,255,255,.72);font-size:11px;line-height:1.25;max-width:290px}
          @media(max-width:600px){.tscheppa-map-mode{left:8px;top:max(8px,env(safe-area-inset-top));max-width:calc(100vw - 16px)}.tscheppa-map-mode button{min-height:42px;padding:0 11px}.tscheppa-map-mode__status{max-width:calc(100vw - 42px)}}
        `;
        document.head.appendChild(style);
        this._standaloneModeStyle = style;

        const controls = document.createElement('section');
        controls.className = 'tscheppa-map-mode';
        controls.setAttribute('aria-label', 'Kartenmodus');
        controls.dataset.mode = 'presenter';
        controls.dataset.hasFix = 'false';

        const presenter = document.createElement('button');
        presenter.type = 'button';
        presenter.textContent = 'Präsentieren';
        presenter.setAttribute('aria-pressed', 'true');
        presenter.addEventListener('click', () => this.setStandaloneMapMode('presenter'));

        const onsite = document.createElement('button');
        onsite.type = 'button';
        onsite.textContent = 'Vor Ort';
        onsite.setAttribute('aria-pressed', 'false');
        onsite.addEventListener('click', () => this.setStandaloneMapMode('onsite'));

        const locate = document.createElement('button');
        locate.type = 'button';
        locate.className = 'tscheppa-map-mode__locate';
        locate.textContent = '⌖ Zu mir';
        locate.setAttribute('aria-label', 'Kamera zu meinem Standort bewegen');
        locate.addEventListener('click', () => this.focusStandaloneLocation());

        const status = document.createElement('span');
        status.className = 'tscheppa-map-mode__status';
        status.setAttribute('role', 'status');
        status.textContent = 'POI-Präsentation';

        controls.append(presenter, onsite, locate, status);
        document.body.appendChild(controls);
        this._standaloneModeControls = controls;
        this._standaloneModeStatus = status;
        this._standalonePresenterButton = presenter;
        this._standaloneOnsiteButton = onsite;
        this._standaloneLocateButton = locate;

        if (new URLSearchParams(window.location.search).get('mode') === 'onsite') {
            this.setStandaloneMapMode('onsite');
        }
    }

    private setStandaloneMapMode(mode: 'presenter' | 'onsite'): void {
        const previousMode = this._standaloneMode;
        if (mode === 'onsite' && previousMode !== 'onsite') {
            const distance = (this.appSettings.cameraDistance.targetValue ?? this.appSettings.cameraDistance.value) as number;
            const latitude = (this.appSettings.cameraLatitude.targetValue ?? this.appSettings.cameraLatitude.value) as number;
            this._presenterCameraSnapshot = { distance, latitude };
        }
        this._standaloneMode = mode;
        if (this._standaloneModeControls) this._standaloneModeControls.dataset.mode = mode;
        this._standalonePresenterButton?.setAttribute('aria-pressed', String(mode === 'presenter'));
        this._standaloneOnsiteButton?.setAttribute('aria-pressed', String(mode === 'onsite'));
        if (mode === 'presenter') {
            this.stopStandaloneGeolocation();
            this.applyUserPosition(null);
            if (this._presenterCameraSnapshot) {
                this.appSettings.cameraDistance.value = this._presenterCameraSnapshot.distance;
                this.appSettings.cameraLatitude.value = this._presenterCameraSnapshot.latitude;
                this._presenterCameraSnapshot = null;
            }
            this.setStandaloneModeStatus('POI-Präsentation');
            return;
        }
        this.startStandaloneGeolocation();
    }

    private startStandaloneGeolocation(): void {
        if (!navigator.geolocation) {
            this.setStandaloneModeStatus('Standort ist in diesem Browser nicht verfügbar.');
            return;
        }
        if (this._geolocationWatchId !== null) {
            this.focusStandaloneLocation();
            return;
        }
        this._geolocationInitialFocus = true;
        this.setStandaloneModeStatus('Standortfreigabe wird angefragt …');
        this._geolocationWatchId = navigator.geolocation.watchPosition(
            position => {
                if (this._standaloneMode !== 'onsite') return;
                const coords = position.coords;
                const fix = {
                    lat: coords.latitude,
                    lng: coords.longitude,
                    heading: coords.heading === null ? undefined : coords.heading,
                    accuracy: Number.isFinite(coords.accuracy) ? coords.accuracy : undefined,
                };
                this._lastStandaloneFix = fix;
                const onMap = this.applyUserPosition(fix);
                if (this._standaloneModeControls) this._standaloneModeControls.dataset.hasFix = 'true';
                const accuracy = fix.accuracy !== undefined ? ` · ±${Math.round(fix.accuracy)} m` : '';
                this.setStandaloneModeStatus(onMap ? `Standort live${accuracy}` : `Standort außerhalb des 3D-Ausschnitts${accuracy}`);
                if (this._geolocationInitialFocus && onMap) {
                    this._geolocationInitialFocus = false;
                    this.focusStandaloneLocation();
                }
            },
            error => {
                const message = error.code === error.PERMISSION_DENIED
                    ? 'Standortfreigabe wurde nicht erteilt.'
                    : error.code === error.POSITION_UNAVAILABLE
                        ? 'GPS-Position ist gerade nicht verfügbar.'
                        : 'GPS-Suche hat zu lange gedauert.';
                this.setStandaloneModeStatus(message);
                this.stopStandaloneGeolocation();
            },
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
        );
    }

    private stopStandaloneGeolocation(): void {
        if (this._geolocationWatchId !== null && navigator.geolocation) {
            navigator.geolocation.clearWatch(this._geolocationWatchId);
        }
        this._geolocationWatchId = null;
        this._geolocationInitialFocus = false;
        this._lastStandaloneFix = null;
        if (this._standaloneModeControls) this._standaloneModeControls.dataset.hasFix = 'false';
    }

    private focusStandaloneLocation(): void {
        const fix = this._lastStandaloneFix;
        if (!fix) {
            this.setStandaloneModeStatus('GPS sucht deinen Standort …');
            return;
        }
        // On-site mode is an orientation view, not a close-up presentation shot.
        this.appSettings.cameraLatitude.value = 62;
        this.bridgeFlyTo(fix.lat, fix.lng, 0.32);
    }

    private setStandaloneModeStatus(message: string): void {
        if (this._standaloneModeStatus) this._standaloneModeStatus.textContent = message;
    }

    private installStandaloneTapHandler(): void {
        if (this._standaloneTapCleanup) return;
        const canvas = (this.scene as any)?.getRenderer?.()?.domElement as HTMLCanvasElement | undefined;
        if (!canvas) return;
        let downX = 0, downY = 0, downT = 0;
        const onDown = (event: PointerEvent) => {
            downX = event.clientX; downY = event.clientY; downT = performance.now();
        };
        const onUp = (event: PointerEvent) => {
            if (Math.hypot(event.clientX - downX, event.clientY - downY) > 12) return;
            if (performance.now() - downT > 400) return;
            const id = this.pickBridgePin(event.clientX, event.clientY);
            const pin = id === null ? null : this._standalonePins.get(id);
            const poi = id === null ? null : this._bridgePins.get(id);
            if (!pin || !poi) return;
            this.setActivePOI(poi);
            this.showStandalonePin(pin);
        };
        canvas.addEventListener('pointerdown', onDown, { passive: true });
        canvas.addEventListener('pointerup', onUp, { passive: true });
        this._standaloneTapCleanup = () => {
            canvas.removeEventListener('pointerdown', onDown);
            canvas.removeEventListener('pointerup', onUp);
        };
    }

    private ensureStandaloneSheet(): HTMLElement {
        if (this._standaloneSheet) return this._standaloneSheet;
        const style = document.createElement('style');
        style.textContent = `
          .tscheppa-poi-sheet{position:fixed;z-index:10020;right:18px;bottom:18px;width:min(390px,calc(100vw - 36px));max-height:min(76vh,720px);overflow:auto;box-sizing:border-box;padding:22px;border:1px solid rgba(255,255,255,.22);border-radius:24px;background:rgba(8,18,34,.94);box-shadow:0 24px 80px rgba(0,0,0,.48);backdrop-filter:blur(18px);color:#fff;font-family:Inter,system-ui,sans-serif;display:none}
          .tscheppa-poi-sheet[data-open="true"]{display:block;animation:tscheppa-sheet-in .22s ease-out}
          .tscheppa-poi-sheet__close{position:absolute;right:12px;top:12px;width:38px;height:38px;border:0;border-radius:50%;background:rgba(255,255,255,.12);color:#fff;font-size:25px;line-height:1;cursor:pointer}
          .tscheppa-poi-sheet__eyebrow{margin:0 42px 7px 0;color:#f7c866;font-size:12px;font-weight:750;letter-spacing:.11em;text-transform:uppercase}
          .tscheppa-poi-sheet h2{margin:0 42px 12px 0;font:700 clamp(24px,5vw,34px)/1.05 Georgia,serif}
          .tscheppa-poi-sheet p{margin:0 0 17px;color:rgba(255,255,255,.86);font-size:16px;line-height:1.52;white-space:pre-line}
          .tscheppa-poi-sheet__media{display:grid;gap:12px}
          .tscheppa-poi-sheet img,.tscheppa-poi-sheet video{display:block;width:100%;max-height:300px;object-fit:cover;border-radius:15px;background:#030912}
          .tscheppa-poi-sheet audio{width:100%}
          @keyframes tscheppa-sheet-in{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
          @media(max-width:600px){.tscheppa-poi-sheet{left:8px;right:8px;bottom:8px;width:auto;max-height:62vh;border-radius:22px;padding:20px}.tscheppa-poi-sheet h2{font-size:27px}}
        `;
        document.head.appendChild(style);
        this._standaloneSheetStyle = style;

        const sheet = document.createElement('aside');
        sheet.className = 'tscheppa-poi-sheet';
        sheet.setAttribute('role', 'dialog');
        sheet.setAttribute('aria-modal', 'false');
        const close = document.createElement('button');
        close.className = 'tscheppa-poi-sheet__close';
        close.type = 'button';
        close.setAttribute('aria-label', 'Information schließen');
        close.textContent = '×';
        close.addEventListener('click', () => this.hideStandaloneSheet());
        sheet.appendChild(close);
        document.body.appendChild(sheet);
        this._standaloneSheet = sheet;
        return sheet;
    }

    private hideStandaloneSheet(): void {
        this._standaloneMediaToken++;
        this._standaloneHls?.destroy();
        this._standaloneHls = null;
        if (this._standaloneSheet) this._standaloneSheet.dataset.open = 'false';
    }

    private showStandaloneFeedError(): void {
        const sheet = this.ensureStandaloneSheet();
        while (sheet.children.length > 1) sheet.lastElementChild?.remove();
        const title = document.createElement('h2');
        title.textContent = 'Informationen nicht erreichbar';
        const body = document.createElement('p');
        body.textContent = 'Die 3D-Karte ist geladen, die POI-Informationen konnten aber gerade nicht abgerufen werden.';
        sheet.append(title, body);
        sheet.dataset.open = 'true';
    }

    private showStandalonePin(pin: BridgePin): void {
        const sheet = this.ensureStandaloneSheet();
        this._standaloneHls?.destroy();
        this._standaloneHls = null;
        const token = ++this._standaloneMediaToken;
        while (sheet.children.length > 1) sheet.lastElementChild?.remove();

        const eyebrow = document.createElement('div');
        eyebrow.className = 'tscheppa-poi-sheet__eyebrow';
        eyebrow.textContent = [pin.category, pin.subcategory].filter(Boolean).join(' · ') || 'Tscheppaschlucht';
        const title = document.createElement('h2');
        title.textContent = pin.title || 'Wegpunkt';
        sheet.setAttribute('aria-label', title.textContent);
        sheet.append(eyebrow, title);
        if (pin.description) {
            const description = document.createElement('p');
            description.textContent = pin.description;
            sheet.appendChild(description);
        }

        const mediaRoot = document.createElement('div');
        mediaRoot.className = 'tscheppa-poi-sheet__media';
        const media = pin.media || [];
        const images = media.filter(item => item.role === 'main' && item.mimeType.startsWith('image/')).slice(0, 4);
        for (const item of images) {
            const image = document.createElement('img');
            image.src = MapPinsApp.normalizeMediaUrl(item.url);
            image.alt = pin.title;
            image.loading = 'lazy';
            mediaRoot.appendChild(image);
        }
        for (const item of media.filter(entry => entry.role === 'video' && entry.mimeType.startsWith('video/')).slice(0, 1)) {
            const video = document.createElement('video');
            video.controls = true;
            video.playsInline = true;
            video.preload = 'metadata';
            video.crossOrigin = 'anonymous';
            if (item.thumbnailUrl) video.poster = MapPinsApp.normalizeMediaUrl(item.thumbnailUrl);
            const url = MapPinsApp.normalizeMediaUrl(item.url);
            if (/\.m3u8(?:$|[?#])/i.test(url)) {
                if (video.canPlayType('application/vnd.apple.mpegurl')) {
                    video.src = url;
                } else {
                    import('hls.js').then(({ default: Hls }) => {
                        if (token !== this._standaloneMediaToken || !Hls.isSupported()) return;
                        const hls = new Hls({ enableWorker: true });
                        hls.loadSource(url);
                        hls.attachMedia(video);
                        this._standaloneHls = hls;
                    }).catch(() => {});
                }
            } else {
                video.src = url;
            }
            mediaRoot.appendChild(video);
        }
        for (const item of media.filter(entry => entry.mimeType.startsWith('audio/')).slice(0, 1)) {
            const audio = document.createElement('audio');
            audio.controls = true;
            audio.preload = 'none';
            audio.src = MapPinsApp.normalizeMediaUrl(item.url);
            mediaRoot.appendChild(audio);
        }
        if (mediaRoot.childElementCount > 0) sheet.appendChild(mediaRoot);
        sheet.dataset.open = 'true';
    }

    private static maskInside(glbX: number, glbZ: number): boolean {
        const m = terrainMask as any;
        if (!MapPinsApp._maskBits) {
            const bin = atob(m.bits);
            const arr = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
            MapPinsApp._maskBits = arr;
        }
        const cx = Math.floor((glbX - m.minX) / m.cellW);
        const cz = Math.floor((glbZ - m.minZ) / m.cellH);
        if (cx < 0 || cz < 0 || cx >= m.res || cz >= m.res) return false;
        const b = cz * m.res + cx;
        return (MapPinsApp._maskBits[b >> 3] & (1 << (b & 7))) !== 0;
    }

    // --- TPS residual warp: the hand-modelled GLB is locally distorted vs. reality, so the
    // global linear projection has ~25 m residuals. The snap-to-WPT pairs found in
    // applyBridgePins double as warp anchors (projected pos → residual to the true node),
    // and EVERYTHING geo-positioned (unsnapped pins, routes, user dot, flyTo) goes through
    // the corrected projection so it all shares the same distortion field.
    private _tpsWarp: TpsWarp | null = null;

    /** lat/lng → GLB model x/z through the global projection + TPS residual correction. */
    private geoToGlb(lat: number, lng: number): { x: number; z: number } {
        const g = this._geoTransform!.geoToModel(lat, lng);
        if (this._tpsWarp?.valid) {
            const c = this._tpsWarp.correction(g.x, g.z);
            return { x: g.x + c.dx, z: g.z + c.dz };
        }
        return g;
    }

    /** GLB model x/z → lat/lng, approximately inverting the TPS warp (one fixed-point step —
     *  the correction field varies slowly, so this is well within GPS accuracy). */
    private glbToGeo(x: number, z: number): { lat: number; lng: number } {
        let px = x, pz = z;
        if (this._tpsWarp?.valid) {
            const c = this._tpsWarp.correction(x, z);
            px -= c.dx; pz -= c.dz;
        }
        return this._geoTransform!.modelToGeo(px, pz);
    }

    private startEmbedBridge(mapRoot: THREE.Object3D): void {
        if (!MapBridge.isEmbedActive() || this._bridge) return;
        this.initializeMapSpace(mapRoot);
        this._mapExperience = new MapExperienceController({
            surface: this._mapSurface ?? undefined,
            onInit: (p) => {
                if (p.georef) {
                    // Override the host georef: it was fitted against the OLD GLB space
                    // (Sep-2025 export). GLB 114637 is a fresh meter-space export; this
                    // transform was refitted 2026-07-20 against 26 id-matched node<->latlng
                    // pairs (median residual 16.5 m, max 46.5 m).
                    const georef = MapPinsApp._GLB_114637_GEOREF;
                    this._bridgeGeoref = georef;
                    this._geoTransform = new GeoModelTransform(georef);
                    Logger.Instance.Info(`Bridge init: host georef mpu=${p.georef.metersPerUnit} OVERRIDDEN by frame-fitted georef (mpu=${georef.metersPerUnit.toFixed(4)} rot=${georef.rotationDeg.toFixed(2)})`, 'Bridge');
                } else {
                    this._bridge?.sendError('NO_GEOREF', 'init without georef — lat/lng pins cannot be placed');
                }
            },
            pins: {
                setPins: (pins) => this.applyBridgePins(pins),
                focusPoi: (id) => {
                    const poi = this._bridgePins.get(id);
                    if (poi) this.setActivePOI(poi);
                    else this._bridge?.sendError('POI_NOT_FOUND', `focusPoi(${id})`);
                },
                setActivePoi: (id) => {
                    this._bridgeActivePinId = id;
                    // The active object is a semantic exception to category
                    // filtering: "show on map" must actually show it without
                    // mutating the user's filter selection.
                    if (this._categoryFilter && this._lastBridgePinsPush) {
                        this.applyBridgePins(this._lastBridgePinsPush);
                    }
                    if (id === null) {
                        this.systemState.setActivePOI(null);
                        return;
                    }
                    const poi = this._bridgePins.get(id);
                    if (poi) this.systemState.setActivePOI(poi);
                },
                setCategoryFilter: (categories) => {
                    this._categoryFilter = categories;
                    if (this._lastBridgePinsPush) this.applyBridgePins(this._lastBridgePinsPush);
                },
                refreshSurface: () => {
                    if (this._lastBridgePinsPush) this.applyBridgePins(this._lastBridgePinsPush);
                },
            },
            routes: {
                setRoutes: (routes) => this.applyBridgeRoutes(routes),
                selectRoute: (routeId) => this.applyRouteSelection(routeId !== null ? String(routeId) : null),
                refreshSurface: () => {
                    if (this._lastBridgeRoutes) this.applyBridgeRoutes(this._lastBridgeRoutes);
                },
            },
            userPosition: {
                setPosition: (position) => this.applyUserPosition(position),
                refreshSurface: () => this.applyUserPosition(this._lastUserPos),
            },
            camera: {
                flyTo: (payload) => this.bridgeFlyTo(payload.lat, payload.lng, payload.distance),
                zoomBy: (factor) => {
                    try {
                        const cur = this.appSettings.cameraDistance.value ?? 1.4;
                        this.appSettings.cameraDistance.value = Math.min(2.5, Math.max(0.03, cur / factor));
                    } catch {}
                },
            },
        });
        this._bridge = new MapBridge(this._mapExperience);
        this._bridge.start();

        // Pin tap → poiClicked(id) to host (host opens its sheet + audio).
        // Uses raw pointer events instead of the synthetic 'click': mobile browsers swallow
        // click after touch-orbit gestures, and a tap must be distinguished from a drag.
        try {
            const canvas = (this.scene as any)?.getRenderer?.()?.domElement as HTMLCanvasElement | undefined;
            if (canvas) {
                this._mapTapController = new MapTapController(
                    canvas,
                    (clientX, clientY) => this.pickBridgePin(clientX, clientY),
                    (id) => {
                        Logger.Instance.Info(`Pin tapped: ${id}`, 'Bridge');
                        this._bridge?.sendPoiClicked(id);
                    },
                );
                this._mapTapController.start();
            }
        } catch {}
    }

    /** Container-local position for a pin: contract `model` coords (GLB space) win; else
     *  geo→GLB-model→container-local, with height from a terrain raycast. */
    private resolvePinLocal(pin: BridgePin): THREE.Vector3 | null {
        let local: THREE.Vector3 | null;
        if (pin.model) {
            local = this.glbToLocal(pin.model.x, 0, pin.model.z);
        } else if (pin.lat !== undefined && pin.lng !== undefined && this._mapSurface) {
            local = this._mapSurface.geoToWorld(pin.lat, pin.lng);
        } else {
            return null;
        }
        if (!local || !this._mapSurface?.containsWorld(local.x, local.z)) return null;
        // Points outside the terrain cut-out have no ground — skip them (the ArTrack data
        // covers more area than the GLB island).
        const groundY = this._mapSurface.getWorldY(local.x, local.z);
        if (groundY === null) return null;
        local.y = groundY + 0.008;
        return local;
    }

    /** Terrain height (map-local Y) at a map-local XZ via BVH raycast; null when the point
     *  lies outside the terrain cut-out (no hit). */
    private terrainLocalY(localX: number, localZ: number): number | null {
        const root = this._mapRootObj;
        // Deliberately no fallback to _terrainMeshes: that set also contains the four
        // entrance/Kassa cubes and other visible props. If strict ground identification
        // fails, skipping a pin is safer than floating it on a prop.
        const meshes = this._groundMeshes;
        if (!root || meshes.length === 0) return null;
        // Outside the island footprint = no ground, regardless of what the broad
        // collision-mesh set (skirt, underlay planes) would return.
        const glb = this.glbFromLocal(new THREE.Vector3(localX, 0, localZ));
        if (!MapPinsApp.maskInside(glb.x, glb.z)) return null;
        const world = root.localToWorld(new THREE.Vector3(localX, 0, localZ));
        this._rayOrigin.set(world.x, world.y + 1000, world.z);
        this._terrainRay.set(this._rayOrigin, MapPinsApp._DOWN);
        const hits = this._terrainRay.intersectObjects(meshes, false);
        if (hits.length === 0) return null;
        return root.worldToLocal(hits[0].point.clone()).y;
    }

    /**
     * Cluster nearby amenity/transport POIs into one vertical signpost. Layout is always
     * computed from the complete pin push (not the active category filter), so filtering
     * only changes visibility and never moves the remaining markers.
     */
    private buildCategoryPinLayout(
        pins: BridgePin[],
        resolved: Map<number, THREE.Vector3>,
    ): Map<number, { local: THREE.Vector3; poleHeight: number }> {
        if (!this._mapSurface || !this._markerMetrics) return new Map();
        return buildCategoryStackLayout(pins, resolved, this._mapSurface, this._markerMetrics);
    }

    /**
     * Collapse the dense Tschaukofall/Felsentor/Teufelsbrücke content group onto
     * one signpost. Layout uses the complete push, so filtering never moves cubes.
     */
    private buildGorgeStackLayout(
        pins: BridgePin[],
        resolved: Map<number, THREE.Vector3>,
    ): Map<number, { local: THREE.Vector3; poleHeight: number }> {
        if (!this._mapSurface || !this._markerMetrics) return new Map();
        return buildExplicitStackLayout(pins, resolved, this._mapSurface, {
            includes: isGorgeStackPin,
            rank: gorgeStackRank,
            cubeSize: this._markerMetrics.gorgeCubeSize,
            poleHeight: this._markerMetrics.categoryPoleHeight,
            gapFactor: this._markerMetrics.stackGapFactor,
        });
    }

    private applyBridgePins(pins: BridgePin[]): void {
        this._lastBridgePinsPush = pins;
        // Full-replace semantics per contract (`setPins` replaces the whole set).
        for (const poi of this._bridgePins.values()) {
            try {
                this.sceneManager?.removeSceneNode(poi);
                const idx = this.pois.indexOf(poi);
                if (idx >= 0) this.pois.splice(idx, 1);
            } catch {}
        }
        this._bridgePins.clear();
        this._categoryPinNodes.clear();
        this._gorgeStackPinNodes.clear();
        this._decisionPinNodes.clear();
        this._iconPinPoles.clear();

        // --- Snap-to-WPT (post-4263 decision): waypoints that correspond to a hand-placed
        // WPT node in the GLB take the NODE's model position — Alex placed those directly on
        // the model terrain, which beats the ~25m residual of the global geo projection.
        // Greedy 1:1 assignment (each WPT node used once) within a 30-GLB-unit (~35 m) radius;
        // unmatched pins keep the georef projection.
        const snapTarget = new Map<number, { x: number; y: number; z: number }>(); // pin.id -> GLB pos
        if (this._geoTransform) {
            const pinGlb = new Map<number, { x: number; z: number }>();
            for (const pin of pins) {
                if (pin.model || pin.lat === undefined || pin.lng === undefined) continue;
                pinGlb.set(pin.id, this._geoTransform.geoToModel(pin.lat, pin.lng));
            }

            // Explicit mode (post-4263 adjudication): any wptNode in the push means the host
            // ships the reviewed WPT↔waypoint table — trust it verbatim and keep the
            // distance heuristic OFF (it produced the swapped/implausible pairs the review
            // rejected; re-enabling it would resurrect them).
            // ID-join (GLB 114637+): nodes are generated live from ArTrack by the Blender
            // pipeline and carry the waypoint id in their name — pin.id ↔ node.artrackId is
            // deterministic by construction. Supersedes both the adjudicated wptNode table
            // and the distance heuristic (their old WPT numbering no longer applies).
            const byArtrackId = new Map<number, { x: number; y: number; z: number }>();
            // Legacy fallback extracted from the last ID-bearing export.
            for (const e of wptGlbPositions as any[]) if (e.artrackId) byArtrackId.set(e.artrackId, e);
            // Preferred path for every future export: read the real waypoint id directly
            // from WPT-<running>-<artrackId>-SPHERE and convert the runtime node back into
            // GLB file space. No JSON refresh or manual assignment table is then required.
            let runtimeIdNodes = 0;
            const requestedIds = new Set(pins.map(pin => pin.id));
            this._mapRootObj?.traverse((node: THREE.Object3D) => {
                const match = (node.name || '').match(/^WPT-(\d+)-(\d+)-SPHERE$/i);
                if (!match) return;
                const artrackId = Number(match[2]);
                if (!requestedIds.has(artrackId)) return; // ignores the legacy shared 1365 suffix
                const world = node.getWorldPosition(new THREE.Vector3());
                const local = this._mapRootObj!.worldToLocal(world.clone());
                const glb = this.glbFromLocal(local);
                byArtrackId.set(artrackId, { x: glb.x, y: glb.y, z: glb.z });
                runtimeIdNodes++;
            });
            const explicitPins = pins.filter(p => p.wptNode !== undefined);
            if (byArtrackId.size > 0) {
                for (const pin of pins) {
                    const n = byArtrackId.get(pin.id);
                    if (n) snapTarget.set(pin.id, { x: n.x, y: n.y, z: n.z });
                }
                if (explicitPins.length > 0) {
                    Logger.Instance.Warn('Bridge: wptNode field ignored — GLB nodes carry artrack ids, join is by pin id', 'Bridge');
                }
                Logger.Instance.Info(`Bridge: artrack-id join — ${snapTarget.size}/${pins.length} pins on generated node positions (${runtimeIdNodes} read from active GLB)`, 'Bridge');
            } else if (explicitPins.length > 0) {
                for (const pin of explicitPins) {
                    const w = (wptGlbPositions as any[]).find(e => e.wpt === pin.wptNode);
                    if (!w) {
                        this._bridge?.sendError('WPT_NODE_UNKNOWN', `pin ${pin.id} references wptNode ${pin.wptNode}`);
                        continue;
                    }
                    snapTarget.set(pin.id, { x: w.x, y: w.y, z: w.z });
                }
                Logger.Instance.Info(`Bridge: explicit WPT table — ${snapTarget.size}/${explicitPins.length} pins on node positions, heuristic snap OFF`, 'Bridge');
            } else {
                // Heuristic fallback until the host ships the table: greedy 1:1 within
                // ~35 m, poi-only (species snapping onto curated sight nodes — and polluting
                // the TPS anchors — would be worse than the projection offset it fixes).
                const SNAP_GLB_UNITS = 30;
                const cands: { d: number; pinId: number; wpt: number }[] = [];
                for (const pin of pins) {
                    if (pin.kind !== 'poi') continue;
                    const g = pinGlb.get(pin.id);
                    if (!g) continue;
                    for (const w of wptGlbPositions as any[]) {
                        const d = Math.hypot(g.x - w.x, g.z - w.z);
                        if (d < SNAP_GLB_UNITS) cands.push({ d, pinId: pin.id, wpt: w.wpt });
                    }
                }
                cands.sort((a, b) => a.d - b.d);
                const usedPins = new Set<number>(); const usedWpts = new Set<number>();
                const snapLog: string[] = [];
                const mpu = this._bridgeGeoref?.metersPerUnit ?? 1;
                for (const c of cands) {
                    if (usedPins.has(c.pinId) || usedWpts.has(c.wpt)) continue;
                    usedPins.add(c.pinId); usedWpts.add(c.wpt);
                    const w = (wptGlbPositions as any[]).find(e => e.wpt === c.wpt)!;
                    snapTarget.set(c.pinId, { x: w.x, y: w.y, z: w.z });
                    const title = pins.find(p => p.id === c.pinId)?.title ?? '';
                    snapLog.push(`WPT${c.wpt} ↔ pin ${c.pinId} "${title}" (${(c.d * mpu).toFixed(1)} m)`);
                }
                Logger.Instance.Info(`Bridge: snap-to-WPT matched ${snapTarget.size}/${pins.length} pins (poi-only, 1:1, <${SNAP_GLB_UNITS} GLB units)`, 'Bridge');
                // Per-pair log = draft of the explicit WPT↔waypoint mapping table (Tschepp
                // reviews it once, then ships wptNode and this heuristic stays dormant).
                for (const line of snapLog) Logger.Instance.Info(`Bridge SnapMap: ${line}`, 'Bridge');
            }

            // Snap pairs double as TPS anchors: anchor = raw projection, value = residual to
            // the hand-placed node. Every additional waypoint Alex places improves the field.
            const anchors: TpsAnchor[] = [];
            for (const [pinId, wpt] of snapTarget) {
                const proj = pinGlb.get(pinId);
                if (!proj) continue;
                // Off-island nodes (no ground underneath) must not shape the warp field.
                const l = this.glbToLocal(wpt.x, wpt.y, wpt.z);
                if (this.terrainLocalY(l.x, l.z) === null) continue;
                anchors.push({ x: proj.x, z: proj.z, dx: wpt.x - proj.x, dz: wpt.z - proj.z });
            }
            this._tpsWarp = anchors.length >= 4 ? new TpsWarp(anchors, 45) : null;
            if (this._tpsWarp?.valid) {
                const mags = anchors.map(a => Math.hypot(a.dx, a.dz) * (this._bridgeGeoref?.metersPerUnit ?? 1)).sort((a, b) => a - b);
                Logger.Instance.Info(`Bridge: TPS warp built from ${anchors.length} anchors — corrected residuals: median ${mags[Math.floor(mags.length / 2)].toFixed(1)} m, max ${mags[mags.length - 1].toFixed(1)} m`, 'Bridge');
                // Re-project layers rendered before the warp existed.
                if (this._lastBridgeRoutes?.length) this.applyBridgeRoutes(this._lastBridgeRoutes);
                if (this._lastUserPos) this.applyUserPosition(this._lastUserPos);
            }
        }

        let placed = 0;
        let geoSkipped = 0;
        let outsideCut = 0;
        const resolvedPins = new Map<number, THREE.Vector3>();
        for (const pin of pins) {
            const hasGeo = pin.lat !== undefined && pin.lng !== undefined;
            if (!hasGeo && !pin.model) continue;
            if (hasGeo && !pin.model && !this._geoTransform) continue;
            let local: THREE.Vector3 | null;
            const snap = snapTarget.get(pin.id);
            if (snap) {
                local = this.glbToLocal(snap.x, snap.y, snap.z);
                const groundY = this.terrainLocalY(local.x, local.z);
                if (groundY !== null) local.y = groundY + 0.004;
                else local = null;
            } else {
                local = this.resolvePinLocal(pin);
            }
            if (local) resolvedPins.set(pin.id, local);
        }
        const categoryLayout = this.buildCategoryPinLayout(pins, resolvedPins);
        const gorgeLayout = this.buildGorgeStackLayout(pins, resolvedPins);
        this._isBulkLoading = true;
        try {
            for (const pin of pins) {
                // Contract error taxonomy (v0.2 clarification): PIN_NO_POSITION only when BOTH
                // lat/lng and model are missing (host data error). "Position present but not
                // projectable because georef is missing" is a SINGLE NO_GEOREF report, not
                // per-pin spam.
                if (
                    this._categoryFilter
                    && pin.id !== this._bridgeActivePinId
                    && !this._categoryFilter.includes(pin.category ?? '')
                ) continue;
                const hasGeo = pin.lat !== undefined && pin.lng !== undefined;
                if (!hasGeo && !pin.model) {
                    this._bridge?.sendError('PIN_NO_POSITION', `pin ${pin.id}`);
                    continue;
                }
                if (hasGeo && !pin.model && !this._geoTransform) { geoSkipped++; continue; }
                const markerKind = classifyMapMarker(pin);
                const categoryCube = markerKind === 'category-cube';
                const decisionMarker = markerKind === 'decision';
                const gorgeStackMarker = markerKind === 'stack';
                const iconMarker = markerKind === 'icon-plate';
                const customMapMarker = categoryCube || decisionMarker || gorgeStackMarker;
                const mediaTile = markerKind === 'species-media';
                const pinScale = (mediaTile ? 0.035 : 0.018) * (pin.balloonScale ?? 1);
                let local = resolvedPins.get(pin.id)?.clone() ?? null;
                if (!local) { outsideCut++; continue; }
                const categoryPlacement = categoryLayout.get(pin.id);
                const gorgePlacement = gorgeLayout.get(pin.id);
                if (categoryCube && categoryPlacement) {
                    local = categoryPlacement.local.clone();
                } else if (gorgeStackMarker && gorgePlacement) {
                    local = gorgePlacement.local.clone();
                } else if (decisionMarker && this._markerMetrics) {
                    local.y += this._markerMetrics.decisionPoleHeight + this._markerMetrics.decisionSignSize * 0.5;
                } else if (iconMarker && this._markerMetrics) {
                    local.y += this._markerMetrics.iconPoleHeight + this._markerMetrics.iconSignSize * 0.5;
                } else {
                    // Anchor the BOTTOM edge on the terrain: geometry is centered, so lift by
                    // half the pin height (Alex device test: cards were half-sunk into the ground).
                    local.y += pinScale * 0.55;
                }

                const pinData = new POIData(this.sceneSettings, this.appSettings);
                // Map amenities/transport use compact category cubes. Their media is still
                // attached to BridgePin and rendered in the tap sheet, never on the marker.
                pinData.shape.value = customMapMarker ? 'none' : (mediaTile || iconMarker) ? 'tile' : 'hexagon';
                if (mediaTile && pin.thumbnailUrl) pinData.uri.value = [MapPinsApp.normalizeMediaUrl(pin.thumbnailUrl)];
                if (iconMarker && pin.iconUrl) {
                    // A real Presenter POIShape, not a parallel custom marker mesh.
                    // TileShape consumes these exact engine properties and creates
                    // rounded corners, bevelled edges and physical depth.
                    pinData.uri.value = [MapPinsApp.normalizeMediaUrl(pin.iconUrl)];
                    // Square icon assets are authored as circular PNG badges. A radius of
                    // exactly half the side turns the existing bevelled TileShape into a
                    // true circle without introducing a separate marker implementation.
                    pinData.cornerRadius.value = 0.5;
                    pinData.edgeRadius.value = 0.035;
                    pinData.depth.value = 0.14;
                    pinData.aspectRatio.value = 1;
                    pinData.useBacksideSideMaterial.value = true;
                    pinData.enableBillboard.value = true;
                }
                if (pin.color) { try { pinData.color.value = new Color(pin.color); } catch {} }
                // 0.035 base: readable from the embed overview camera (0.01 was pixel-sized
                // at bird's-eye distance). Phase ③ replaces this with LOD/force-graph sizing.
                pinData.scale.value = customMapMarker ? 1 : iconMarker ? (this._markerMetrics?.iconSignSize ?? 0.031) : pinScale;
                if (!iconMarker) pinData.depth.value = 0.015;
                pinData.markdown.value = pin.title || '';
                // Labels render from html.value (markdown alone is never converted here)
                const safeTitle = (pin.title || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                pinData.html.value = safeTitle;
                // Category cubes communicate through their pictogram. Rendering the old
                // canvas title beside a multi-cube stack recreates the same overlap this
                // marker replaces; the title and media appear in the tap detail sheet.
                pinData.showLabel.value = !!pin.title && !categoryCube && !gorgeStackMarker;
                pinData.labelFontSize.value = 14;
                pinData.labelBackgroundColor.value = new Color('#00000000');
                pinData.localTranslationOffset.value = new Vector3(local.x, local.y, local.z);

                const poi = new POI(`BridgePin_${pin.id}`, pinData, this.sceneSettings, this.appSettings, this.getLayoutData());
                if (categoryCube && categoryPlacement) {
                    const categoryNode = new MapCategoryPinNode(
                        pin,
                        (this._markerMetrics?.categoryCubeSize ?? 0.024) * (pin.balloonScale ?? 1),
                        categoryPlacement.poleHeight,
                    );
                    poi.addChild(categoryNode);
                    this._categoryPinNodes.set(pin.id, categoryNode);
                } else if (gorgeStackMarker && gorgePlacement) {
                    const stackNode = new MapPoiStackPinNode(
                        pin,
                        this._markerMetrics?.gorgeCubeSize ?? 0.018,
                        gorgePlacement.poleHeight,
                        pin.iconUrl ? MapPinsApp.normalizeMediaUrl(pin.iconUrl) : undefined,
                    );
                    poi.addChild(stackNode);
                    this._gorgeStackPinNodes.set(pin.id, stackNode);
                } else if (decisionMarker) {
                    const decisionNode = new MapDecisionPinNode(
                        pin,
                        this._markerMetrics?.decisionSignSize ?? 0.038,
                        this._markerMetrics?.decisionPoleHeight ?? 0.043,
                    );
                    poi.addChild(decisionNode);
                    this._decisionPinNodes.set(pin.id, decisionNode);
                } else if (iconMarker) {
                    const pole = new MapMarkerPoleNode(
                        `IconPole_${pin.id}`,
                        -(this._markerMetrics?.iconSignSize ?? 0.031) * 0.5,
                        -(this._markerMetrics?.iconSignSize ?? 0.031) * 0.5 - (this._markerMetrics?.iconPoleHeight ?? 0.036),
                        (this._markerMetrics?.iconSignSize ?? 0.031) * 0.01,
                        pin.id,
                    );
                    poi.addChild(pole);
                    this._iconPinPoles.set(pin.id, pole);
                }
                this.addPOI(poi);
                this._bridgePins.set(pin.id, poi);
                placed++;
            }
        } finally {
            this._isBulkLoading = false;
        }
        if (this.layout && this.pois.length > 0) this.applyLayoutWithProcessCurveUpdate();
        if (geoSkipped > 0 && !this._geoTransform) {
            this._bridge?.sendError('NO_GEOREF', `${geoSkipped} geo-positioned pins skipped — awaiting georef in init`);
        }
        Logger.Instance.Info(`Bridge: placed ${placed}/${pins.length} pins (${this._categoryPinNodes.size} category cubes, ${this._gorgeStackPinNodes.size} gorge-stack cubes, ${this._decisionPinNodes.size} decision signs, ${this._iconPinPoles.size} Presenter icon shapes)${geoSkipped ? ` (${geoSkipped} awaiting georef)` : ''}${outsideCut ? ` (${outsideCut} outside terrain cut-out)` : ''}`, 'Bridge');
    }

    private _selectedRouteId: string | null = null;
    // Cached for re-projection when the TPS warp (re)builds — routes/user pos may arrive
    // before setPins, and the warp only exists after the snap pairs are known.
    private _lastBridgeRoutes: BridgeRoute[] | null = null;
    private _lastUserPos: { lat: number; lng: number; heading?: number; accuracy?: number } | null = null;

    private applyBridgeRoutes(routes: BridgeRoute[]): void {
        this._lastBridgeRoutes = routes;
        this._routeRenderer?.setRoutes(routes);
        Logger.Instance.Info(`Bridge: rendered ${routes.length} routes`, 'Bridge');
    }

    private applyUserPosition(pos: { lat: number; lng: number; heading?: number; accuracy?: number } | null): boolean {
        this._lastUserPos = pos;
        return this._userPositionRenderer?.setPosition(pos) ?? false;
    }

    /** 2D semantics mirrored: the selected route renders fully saturated, others dimmed. */
    private applyRouteSelection(routeId: string | null): void {
        this._selectedRouteId = routeId;
        this._routeRenderer?.selectRoute(routeId);
    }

    private bridgeFlyTo(lat: number, lng: number, distance?: number): void {
        if (!this._mapSurface) return;
        const l = this._mapSurface.geoToWorld(lat, lng);
        if (!l) return;
        const x = l.x, z = l.z;
        const y = this._mapSurface.getWorldY(x, z) ?? 0;
        // Reuse the pin flow: a persistent invisible target POI whose offset we move.
        let target = this._bridgePins.get(-999999);
        if (!target) {
            const d = new POIData(this.sceneSettings, this.appSettings);
            d.shape.value = 'none';
            d.showLabel.value = false;
            d.localTranslationOffset.value = new Vector3(x, y, z);
            target = new POI('BridgeFlyTarget', d, this.sceneSettings, this.appSettings, this.getLayoutData());
            this.addPOI(target);
            this._bridgePins.set(-999999, target);
        } else {
            target.poidata.localTranslationOffset.value = new Vector3(x, y, z);
            if (this.layout) this.applyLayoutWithProcessCurveUpdate();
        }
        if (distance !== undefined) { try { this.appSettings.cameraDistance.value = distance; } catch {} }
        this.setActivePOI(target);
    }

    /** Raycast the bridge-pin meshes at a screen position; returns pin id or null. */
    private pickBridgePin(clientX?: number, clientY?: number): number | null {
        if (clientX === undefined || clientY === undefined || this._bridgePins.size === 0) return null;
        const threeCam = (this.scene as any)?.camera as THREE.Camera | undefined;
        const canvas = (this.scene as any)?.getRenderer?.()?.domElement as HTMLCanvasElement | undefined;
        if (!threeCam || !canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((clientX - rect.left) / rect.width) * 2 - 1,
            -((clientY - rect.top) / rect.height) * 2 + 1
        );
        const ray = new THREE.Raycaster();
        ray.setFromCamera(ndc, threeCam);
        // Prefer the actual visible marker geometry. Several legacy POIs carry
        // oversized/invisible helper meshes, so raycasting the POI roots first can
        // make a tap on one gorge cube select an unrelated earlier station.
        let taggedHitId: number | null = null;
        let taggedHitDistance = Number.POSITIVE_INFINITY;
        for (const poi of this._bridgePins.values()) {
            for (const root of poi.getThreeObjects?.() || []) {
                root?.traverse(object => {
                    const taggedId = object.userData?.bridgePinId;
                    if (typeof taggedId !== 'number' || !(object as THREE.Mesh).isMesh) return;
                    const hit = ray.intersectObject(object, false)[0];
                    if (hit && hit.distance < taggedHitDistance) {
                        taggedHitDistance = hit.distance;
                        taggedHitId = taggedId;
                    }
                });
            }
        }
        if (taggedHitId !== null) return taggedHitId;

        let rayHitId: number | null = null;
        let rayHitDistance = Number.POSITIVE_INFINITY;
        for (const [id, poi] of this._bridgePins) {
            if (id === -999999) continue;
            const objs = poi.getThreeObjects?.() || [];
            for (const o of objs) {
                if (!o) continue;
                // POIs are stored in presentation order, not depth order. Returning the
                // first intersecting POI made a foreground cube select an unrelated marker
                // that happened to occur earlier in the feed. Resolve all hits and keep
                // the physically closest object instead.
                const hit = ray.intersectObject(o, true)[0];
                if (hit && hit.distance < rayHitDistance) {
                    rayHitDistance = hit.distance;
                    rayHitId = id;
                }
            }
        }
        if (rayHitId !== null) return rayHitId;
        // Screen-space fallback: nearest pin within 36 px of the tap — small pins at steep
        // angles are nearly impossible to ray-hit on a phone.
        let bestId: number | null = null; let bestD = 36;
        const v = new THREE.Vector3();
        for (const [id, poi] of this._bridgePins) {
            if (id === -999999) continue;
            const wp = (poi as any).transform?.getWorldPosition?.();
            if (!wp) continue;
            v.set(wp.x, wp.y, wp.z).project(threeCam);
            if (v.z > 1) continue;
            const sx = rect.left + (v.x * 0.5 + 0.5) * rect.width;
            const sy = rect.top + (-v.y * 0.5 + 0.5) * rect.height;
            const d = Math.hypot(sx - clientX, sy - clientY);
            if (d < bestD) { bestD = d; bestId = id; }
        }
        return bestId;
    }

    protected override onDispose(): void {
        this._bridge?.stop();
        this._standaloneAbort?.abort();
        this.stopStandaloneGeolocation();
        this._standaloneTapCleanup?.();
        this.hideStandaloneSheet();
        this._standaloneSheet?.remove();
        this._standaloneSheetStyle?.remove();
        this._standaloneModeControls?.remove();
        this._standaloneModeStyle?.remove();
        this._standaloneSheet = null;
        this._standaloneSheetStyle = null;
        this._standaloneModeControls = null;
        this._standaloneModeStyle = null;
        super.onDispose();
    }
}

export default MapPinsApp;
